import { App } from 'ionic-angular';
import { ClientServiceProvider } from '../../../clients/services/client-service/client-service';
import { ConfigServiceProvider } from '../../../core/services/config-service/config-service';
import { DataSource } from '../../interfaces/data-source.interface';
import { DataSourceConfig } from '../../interfaces/data-source-config.interface';
import { ENVIRONMENT } from '../../../../../environments/environment';
import { FilterOutput } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filter-outpt.interface';
import { FiltersConfig } from '../../../../submodules/sellions_layout_renderer/projects/sellions-layout-renderer-lib/src/lib/interfaces/filters-config.interface';
import { HttpClient, HttpParams } from '@angular/common/http';
import { IClientModel } from '../../../clients/interfaces/i-client-model.interface';
import { Injectable } from '@angular/core';
import { IRecipient } from '../../../clients/interfaces/i-recipient.interface';
import { map, take } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { PlayerServiceProvider } from '../../../core/services/player-service/player-service';
import { SellionsCalendarEvent } from '../../interfaces/sellions-calendar-event.interface';
import { SellionsEvent } from '../events-service/events-service';

@Injectable()
export class EventsDataSource implements DataSource {
  public eventLayout: string;
  private lastSynchronizationTime: Date;
  private readonly synchronizeTimeDiff = ENVIRONMENT.eventsSynchronizeTimeDifferenceMiliseconds;
  private config: DataSourceConfig = {} as DataSourceConfig;
  private _filters: FilterOutput[] = [];

  constructor(
    private app: App,
    private configService: ConfigServiceProvider,
    private clientsService: ClientServiceProvider,
    private player: PlayerServiceProvider,
    private http: HttpClient,
  ) {}

  public get filters(): FilterOutput[] {
    return this._filters;
  }

  public getEvents(): Promise<SellionsCalendarEvent[]> {
    const user = this.player.player$.value;
    this.synchronize();

    if (user)
      return this.getRawEvents({ userId: user && user.id })
        .pipe(
          take(1),
          map((events) => this.mapToSellionsCalendarEvents(events)),
        )
        .toPromise();
    else return Promise.reject('no user avaible in events-data-source');
  }

  public filter(filters: FilterOutput[]): Promise<SellionsCalendarEvent[]> {
    this._filters = filters;
    const payload = this.prepareFilterPayload(this.mapFiltersToDictionary(filters));
    return this.getRawEvents(payload)
      .pipe(
        take(1),
        map((events) => this.mapToSellionsCalendarEvents(events)),
      )
      .toPromise();
  }

  public search(query: string): Promise<SellionsCalendarEvent[]> {
    return this.filter([{ type: 'input', value: query, column: 'searchQuery', queryValue: null }]);
  }

  private mapFiltersToDictionary(filters: FilterOutput[]): { [key: string]: string } {
    const dictionary = {};
    filters.forEach((filter) => (dictionary[filter.column] = filter.value));
    return dictionary;
  }

  private getRawEvents(params: any): Observable<SellionsEvent[]> {
    let url = this.configService.getUrl('eventsGet');
    return this.http.get<SellionsEvent[]>(url, { params });
  }

  private mapToSellionsCalendarEvents(events: SellionsEvent[]): SellionsCalendarEvent[] {
    return events.map((event) => {
      const client: IClientModel = this.clientsService.getById(event.clientId);
      return {
        displayComponent: 'SellionsEventComponent',
        displayComponentData: {
          event,
          client,
          recipient: this.getRecipient(event, client),
        },
        dateFrom: event.startDate,
        dateTo: event.endDate,
        dataSource: this,
      };
    });
  }

  public setConfig(config: DataSourceConfig) {
    this.config = config;
  }

  private getRecipient(event: SellionsEvent, client: IClientModel): IRecipient {
    let recipient: IRecipient;
    if (client && client.recipients && client.recipients.length) {
      recipient = client.recipients.find((recipient) => recipient.id === event.recipientId);
    }

    return recipient;
  }

  public async detailsOnClick(event: SellionsCalendarEvent) {
    return this.app.getRootNav().push(this.config.detailsOnClick.component, { ...this.config.detailsOnClick.componentData, event, calendar: true });
  }

  private prepareFilterPayload(filters: { [key: string]: string | number }): HttpParams {
    let payload: HttpParams = {} as HttpParams,
      userId: number;

    if (filters.sort) {
      payload['sort'] = filters.sort;
    }
    if (payload['sort'] === 'date') {
      payload['sort'] = 'startDate';
    }

    if (filters.order) {
      payload['dir'] = filters.order;
    }

    if (filters.manager > -1 && filters.user === -1) {
      userId = filters.manager as number;
    } else if (filters.user !== -1) {
      userId = filters.user as number;
    } else {
      userId = this.player.player.id;
    }

    if (!userId) {
      userId = this.player.player['id'];
    }

    if (filters.searchQuery) {
      payload['searchQuery'] = '%' + filters.searchQuery + '%';
    }

    if (filters.state) {
      payload['state'] = filters.state;
    }

    payload['userId'] = userId;

    if (filters.month) {
      payload['month'] = filters.month;
    }

    if (filters.date) {
      let date = new Date();
      if (filters.date === '>90') {
        date = new Date(date.getTime() - 1000 * 60 * 60 * 24 * 90);
        payload['dateBefore'] = date.toISOString();
      } else {
        date = new Date(date.getTime() - 1000 * 60 * 60 * 24 * (filters.date as number));
        payload['dateAfter'] = date.toISOString();
      }
    }

    if (filters.sapId) {
      payload['sapId'] = filters.sapId;
    }

    if (filters.segment) {
      payload['segment'] = filters.segment;
    }

    return payload;
  }

  public synchronize() {
    let currentTime = Number(new Date());
    let lastUpdate = this.lastSynchronizationTime ? Number(this.lastSynchronizationTime) : 0;

    if (currentTime - lastUpdate > this.synchronizeTimeDiff) {
      this.http
        .post(this.configService.getUrl('synchronize'), {})
        .pipe(take(1))
        .subscribe(() => (this.lastSynchronizationTime = new Date()));
    }
  }

  public getFiltersConfig(): FiltersConfig {
    return this.config.filters;
  }
}



// WEBPACK FOOTER //
// ./src/modules/calendar/services/events-data-source/events-data-source.class.ts